import React from "react";
import { motion } from "framer-motion";
import { APPLY_URL } from "./constants";
import { Check, ArrowUpRight } from "lucide-react";
import FlowerOfLife from "./FlowerOfLife";

const CORE = [
    "Weekly Roundtables",
    "Community Forum Access",
    "Monthly Challenges",
    "Visibility into Interest Circles",
    "Full onboarding experience",
    "'Not A Goofball' badge",
];

const Membership = () => {
    return (
        <section
            id="membership"
            data-testid="membership-section"
            className="relative border-y border-[#d4af37]/15 bg-[#080a1f]"
        >
            <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-12 lg:gap-16">
                <div>
                    <div className="text-xs uppercase tracking-[0.3em] text-[#f2c652] font-display mb-4">
                        VI. Join the Circle
                    </div>
                    <h2 className="font-display font-black tracking-tighter text-4xl md:text-6xl leading-[0.95] text-[#f6f1e1] mb-6">
                        Step into the{" "}
                        <span className="font-serif-italic font-light text-[#f2c652]">
                            circle.
                        </span>
                    </h2>
                    <p className="text-[#f6f1e1]/60 font-body text-base md:text-lg leading-relaxed max-w-xl">
                        The $24.99/month entry point is intentional. It filters
                        for commitment without breaking the bank, and keeps
                        the room full of people who'll actually show up.
                    </p>

                    <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                        {CORE.map((item, i) => (
                            <motion.div
                                key={item}
                                initial={{ opacity: 0, x: -10 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4, delay: i * 0.05 }}
                                className="flex items-start gap-3"
                                data-testid={`benefit-${i + 1}`}
                            >
                                <div className="mt-0.5 w-5 h-5 rounded-full bg-[#d4af37]/15 border border-[#d4af37]/50 flex items-center justify-center shrink-0">
                                    <Check
                                        className="w-3 h-3 text-[#f2c652]"
                                        strokeWidth={3}
                                    />
                                </div>
                                <span className="font-body text-[#f6f1e1]/80 text-[15px]">
                                    {item}
                                </span>
                            </motion.div>
                        ))}
                    </div>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7 }}
                    className="relative"
                    data-testid="membership-card"
                >
                    <div className="relative overflow-hidden rounded-sm border border-[#d4af37]/30 bg-gradient-to-br from-[#141838] to-[#0a0c27] p-8 md:p-10">
                        {/* Flower of life watermark */}
                        <div className="absolute -top-16 -right-16 w-64 h-64 opacity-20 pointer-events-none">
                            <div className="w-full h-full dn-rotate-rev">
                                <FlowerOfLife
                                    color="#d4af37"
                                    opacity={0.7}
                                    strokeWidth={0.5}
                                    className="w-full h-full"
                                />
                            </div>
                        </div>
                        <div className="absolute -top-20 -right-20 w-64 h-64 bg-[#d4af37]/15 rounded-full blur-3xl" />

                        <div className="relative">
                            <div className="flex items-center justify-between mb-6">
                                <span className="font-display text-[11px] uppercase tracking-[0.3em] text-[#f6f1e1]/50">
                                    Membership · Entry Tier
                                </span>
                                <span className="font-display text-[11px] uppercase tracking-[0.3em] text-[#f2c652]">
                                    If Approved
                                </span>
                            </div>
                            <div className="flex items-baseline gap-2 mb-2">
                                <span className="font-display font-black bg-gradient-to-br from-[#f2c652] to-[#fca112] bg-clip-text text-transparent text-6xl md:text-7xl tracking-tighter">
                                    $24.99
                                </span>
                                <span className="font-body text-[#f6f1e1]/50 text-lg">
                                    / month
                                </span>
                            </div>
                            <p className="font-body text-[#f6f1e1]/60 text-sm mb-8">
                                An intentional price point. It filters for
                                commitment, not budget.
                            </p>

                            <a
                                href={APPLY_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="membership-apply-cta"
                                className="group inline-flex w-full items-center justify-center gap-3 bg-gradient-to-br from-[#f2c652] to-[#d4af37] hover:from-[#fcc001] hover:to-[#f2c652] text-[#0a0c27] font-display font-semibold text-base py-4 rounded-sm transition-all hover:-translate-y-0.5 shadow-[0_15px_50px_-12px_rgba(242,198,82,0.55)]"
                            >
                                Apply Now
                                <ArrowUpRight className="w-5 h-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                            </a>

                            <div className="mt-8 pt-8 border-t border-[#d4af37]/15">
                                <div className="font-display text-[11px] uppercase tracking-[0.3em] text-[#1ce5da] mb-3">
                                    Future Opportunities
                                </div>
                                <p className="text-[#f6f1e1]/60 font-body text-sm leading-relaxed">
                                    Access to premium add-ons: mentorship
                                    programs, private events and leadership
                                    paths. Unlock as you grow through the
                                    Layers.
                                </p>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default Membership;
